import { useMemo } from 'react';
import { useGetCountries } from '../../api/countries/use-get-countries';
import { useGetManyWarehouses } from '../../api/warehouses/use-get-many-warehouses';
import { findCountriesById } from '../utils/countries/find-countries-by-id';

export interface IWarehouseGroup {
  type: 'all' | 'country' | 'client' | 'warehouse';
  value: string;
  label: string;
  warehouseIds: number[];
}

interface ICountryMapValue {
  code: string;
  warehouseIds: number[];
}

interface IWarehouseGroupsResult {
  groups: IWarehouseGroup[];
  isLoading: boolean;
}

export const useWarehouseGroups = (): IWarehouseGroupsResult => {
  const { data: warehouses, isLoading: isLoadingWarehouses } = useGetManyWarehouses({ withDeprecated: false });
  const { data: countries, isLoading: isLoadingCountries } = useGetCountries();

  const isLoading = isLoadingWarehouses || isLoadingCountries;

  const groups = useMemo(() => {
    if (!warehouses?.length || !countries?.length) {
      return [];
    }

    const result: IWarehouseGroup[] = [];

    result.push({
      type: 'all',
      value: 'all',
      label: 'all WH',
      warehouseIds: warehouses.map((wh) => wh.id),
    });

    const countryMap = new Map<number, ICountryMapValue>();

    warehouses.forEach((wh) => {
      const countryId = wh.countryId;

      let countryData = countryMap.get(countryId);
      if (!countryData) {
        const country = findCountriesById(countryId, countries);
        countryData = {
          code: country?.code || '',
          warehouseIds: [],
        };
        countryMap.set(countryId, countryData);
      }
      countryData.warehouseIds.push(wh.id);
    });

    countryMap.forEach(({ code, warehouseIds }) => {
      result.push({
        type: 'country',
        value: `country:${code}`,
        label: `all ${code} WH`,
        warehouseIds,
      });
    });

    result.push({
      type: 'client',
      value: 'client',
      label: 'client',
      warehouseIds: [],
    });

    warehouses.forEach((wh) => {
      result.push({
        type: 'warehouse',
        value: `warehouse:${wh.id}`,
        label: wh.name,
        warehouseIds: [wh.id],
      });
    });

    return result;
  }, [warehouses, countries]);

  return { groups, isLoading };
};
